import React from 'react';
import { animated, useSpring } from 'react-spring';
import _merge from 'lodash/merge';
import ChevronDownIcon from '@nokia-csf-uxr/ccfk-assets/ChevronDownIcon';
import IconButton, { IconButtonProps } from 'components/IconButton';
import Styled, { SPRING_CONFIG } from './styles';
import { ExpansionPanelContext } from './';

export interface ExpansionPanelButtonProps extends React.ComponentPropsWithRef<'div'> {
  /** onClick callback. */
  onClick?: React.MouseEventHandler;
  /** onKeyDown callback. */
  onKeyDown?: React.KeyboardEventHandler;
  /** pass additional props to the <IconButton />. */
  iconButtonProps?: Partial<IconButtonProps>;
}

/** Default ExpansionPanel chevron button.
 * You may also use your own button. See demo examples for more information.
 * All additional props will be forwarded to the <IconButton/> element.
 * */
const ExpansionPanelButton = (props: ExpansionPanelButtonProps) => {
  const panelContext = React.useContext(ExpansionPanelContext);
  const {
    onClick, onKeyDown, iconButtonProps, ...otherProps
  } = props;
  const resolvedDisableAnimation = panelContext.disableAnimation;
  const expandButtonStyle = useSpring({
    transform: panelContext.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    config: SPRING_CONFIG,
    immediate: resolvedDisableAnimation
  });
  const { style: userStyle, ...allOtherProps } = otherProps;
  const resolvedStyle = _merge({}, expandButtonStyle, userStyle);
  return (
    <Styled.ExpansionPanelButton as={animated.div} style={resolvedStyle} {...allOtherProps}>
      <IconButton
        aria-label=""
        onClick={(e) => {
          props.onClick && props.onClick(e);
        }}
        onKeyDown={(e) => {
          props.onKeyDown && props.onKeyDown(e);
        }}
        disabled={panelContext.disabled}
        {...iconButtonProps}
      >
        <ChevronDownIcon />
      </IconButton>
    </Styled.ExpansionPanelButton>
  );
};

ExpansionPanelButton.defaultProps = {
  onClick: undefined,
  onKeyDown: undefined,
  iconButtonProps: undefined,
};
ExpansionPanelButton.displayName = 'ExpansionPanelButton';
export default ExpansionPanelButton;
